import {
  faAlarmClock,
  faCircleQuestion,
  faSack,
  faShieldCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { Card, Flex, Skeleton, Text, Tooltip } from '@mantine/core'

import { useTranslation } from '@flecto/translations'
import {
  type CoreEstimateInsuranceForItem,
  type CoreEstimateInsuranceForQuote,
} from '@flecto/types'
import { Icon, TextCurrency } from '@flecto/ui'
import { useTimeUntil } from '@flecto/utils/src/Hooks/useTimeUntil'

import { useFormContext } from '../FormCreateInsurancePolicy/FormCreateInsurancePolicy'

type InsuranceDetailsProps = {
  estimate: CoreEstimateInsuranceForItem
  estimateQuote: CoreEstimateInsuranceForQuote
  rentalValue: number
  localPrice: number
  isLoadingEstimateQuote: boolean
  isRefetchingEstimate: boolean
}

export function InsuranceDetails({
  estimateQuote,
  rentalValue,
  localPrice,
  isLoadingEstimateQuote,
  isRefetchingEstimate,
}: InsuranceDetailsProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const form = useFormContext()

  const startIn = useTimeUntil(form.values.start_at as string)

  const cards = [
    {
      title: t('create_insurance.insurance_details.start_date'),
      value: form.values.start_at_now ? (
        t('create_insurance.dates.start_now')
      ) : (
        <Text>{`${t('create_insurance.dates.start_in')} ${startIn}`}</Text>
      ),

      icon: faAlarmClock,
      hasTooltip: true,
    },
    {
      title: t('create_insurance.insurance_details.total_rental'),
      value:
        localPrice !== 0
          ? localPrice * (form.values.quantity ?? 0)
          : rentalValue * (form.values.quantity ?? 0),
      icon: faSack,
    },
    {
      title: t('create_insurance.insurance_details.est_coverage_cost'),
      value: estimateQuote?.price_cents ?? 0,
      icon: faShieldCheck,
      color: estimateQuote?.price_cents > 0 ? 'green' : 'gray',
    },
  ]

  return (
    <Flex gap='md' wrap='wrap'>
      {cards.map((card, index) => (
        <Card miw={200} key={index} flex={1}>
          <Flex align='center' gap='sm'>
            <Icon
              icon={card.icon}
              withCircleBackground
              size='sm'
              color={card.color}
            />
            <Flex direction='column'>
              <Text c='dimmed' fw={500}>
                {card.title}
              </Text>
              <Flex gap='xs' align='center'>
                {card.title !==
                t('create_insurance.insurance_details.start_date') ? (
                  <Skeleton
                    height={20}
                    width={100}
                    visible={isLoadingEstimateQuote || isRefetchingEstimate}
                  >
                    <TextCurrency
                      cents={Number(card.value) ? Number(card.value) : 0}
                    />
                  </Skeleton>
                ) : (
                  <Text fw={500}>{card.value}</Text>
                )}
                {card.hasTooltip && (
                  <Tooltip
                    label={
                      form.values.start_at_now
                        ? t('create_insurance.insurance_details.tooltip_label')
                        : t(
                            'create_insurance.insurance_details.tooltip_label_start_at',
                            {
                              date: startIn,
                            }
                          )
                    }
                    multiline
                  >
                    <Text component='span' mt={2}>
                      <Icon icon={faCircleQuestion} size='xs' color='dimmed' />
                    </Text>
                  </Tooltip>
                )}
              </Flex>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Flex>
  )
}
